import { useState, useCallback, useEffect, useRef } from "react";
import {
  Upload, FolderOpen, Loader2, FileSpreadsheet, X, CheckCircle2,
  Download, MessageCircle, Trash2, AlertTriangle, FileText,
  RefreshCw, Clock, Phone, ShieldCheck, ChevronLeft, ChevronRight, Plus, Search,
  Layers, Timer, Play, Pause
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { API, apiCall, formatDate } from "@/lib/api";
import { ReverifyCachedPanel } from "./ReverifyCachedPanel";

const ExportBtn = ({ filterType, count, label, icon: Icon, cls, onExport }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button className={cls} size="sm" disabled={count === 0} data-testid={`export-${filterType}`}>
        <Icon className="w-3.5 h-3.5 mr-1.5" />{label} ({count})
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
      <DropdownMenuItem onClick={() => onExport(filterType, "xlsx")} className="text-zinc-300 hover:bg-zinc-800 cursor-pointer text-xs"><FileSpreadsheet className="w-3.5 h-3.5 mr-2" />Excel (.xlsx)</DropdownMenuItem>
      <DropdownMenuItem onClick={() => onExport(filterType, "csv")} className="text-zinc-300 hover:bg-zinc-800 cursor-pointer text-xs"><FileSpreadsheet className="w-3.5 h-3.5 mr-2" />CSV (.csv)</DropdownMenuItem>
      <DropdownMenuItem onClick={() => onExport(filterType, "txt")} className="text-zinc-300 hover:bg-zinc-800 cursor-pointer text-xs"><FileText className="w-3.5 h-3.5 mr-2" />Texto (.txt)</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const StatusBadge = ({ status, progress, queuePosition }) => {
  if (status === "uploading") return (
    <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20" data-testid="status-uploading">
      <Upload className="w-3 h-3 mr-1 animate-pulse" />Procesando archivo...
    </Badge>
  );
  if (status === "queued") return (
    <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20" data-testid="status-queued">
      <Timer className="w-3 h-3 mr-1" />Cola #{queuePosition || "..."}
    </Badge>
  );
  if (status === "processing") return (
    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20" data-testid="status-processing">
      <Clock className="w-3 h-3 mr-1 animate-pulse" />{progress || 0}%
    </Badge>
  );
  if (status === "error") return (
    <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20" data-testid="status-error">
      <AlertTriangle className="w-3 h-3 mr-1" />Error
    </Badge>
  );
  return (
    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20" data-testid="status-completed">
      <CheckCircle2 className="w-3 h-3 mr-1" />Listo
    </Badge>
  );
};

export const BulkTab = ({ focusCampaignId, onFocusHandled, services, isSuperadmin }) => {
  const waEnabled = services?.whatsapp_validation?.enabled || false;
  const [screen, setScreen] = useState("list");
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsCount, setCampaignsCount] = useState(0);
  const [campaignsPage, setCampaignsPage] = useState(1);
  const [campaignsPages, setCampaignsPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMode, setFilterMode] = useState("");
  const [bulkFile, setBulkFile] = useState(null);
  const [campaignName, setCampaignName] = useState("");
  const [campaignMode, setCampaignMode] = useState("phone");
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [previewFilter, setPreviewFilter] = useState("all");
  const [previewPage, setPreviewPage] = useState(1);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [whatsappImporting, setWhatsappImporting] = useState(false);
  const [showWhatsappConfirm, setShowWhatsappConfirm] = useState(false);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("rapidapi");
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);

  const pollingRef = useRef(null);
  const mountedRef = useRef(true);
  const wppPollingRef = useRef(null);
  const prevProcessingRef = useRef(new Set());
  const [waAutoEnabled, setWaAutoEnabled] = useState(true);

  const toggleWaAuto = async () => {
    const newVal = !waAutoEnabled;
    const { ok } = await apiCall("GET", `${API}/admin/platform-settings`);
    if (ok) {
      const { ok: ok2 } = await apiCall("PUT", `${API}/admin/platform-settings`, { wa_auto_trigger_enabled: newVal });
      if (ok2) { setWaAutoEnabled(newVal); toast.success(newVal ? "Verificacion WA activada" : "Verificacion WA pausada"); }
    }
  };

  useEffect(() => {
    // Fetch WA auto-trigger status
    apiCall("GET", `${API}/admin/platform-settings`).then(({ ok, data }) => {
      if (ok) setWaAutoEnabled(data.wa_auto_trigger_enabled !== false);
    }).catch(() => {});
    mountedRef.current = true;
    fetchCampaigns();
    if (waEnabled) {
      apiCall("GET", `${API}/whatsapp/providers`).then(({ ok, data }) => {
        if (ok) setProviders(data.providers || []);
      }).catch(() => {});
    }
    return () => {
      mountedRef.current = false;
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (wppPollingRef.current) clearInterval(wppPollingRef.current);
    };
  }, []);

  useEffect(() => {
    if (focusCampaignId && campaigns.length > 0) {
      const target = campaigns.find(c => c.analysis_id === focusCampaignId);
      if (target) openCampaignDetail(target);
      if (onFocusHandled) onFocusHandled();
    }
  }, [focusCampaignId, campaigns]);

  const playNotificationSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.5);
    } catch {}
  };

  const autoTriggerWhatsapp = async (analysisId) => {
    try {
      const { ok: pOk, data: pData } = await apiCall("GET", `${API}/whatsapp/providers`);
      if (!pOk || !pData.providers?.length) return;
      const provider = pData.providers[0];
      const isRapidApi = provider.id === "rapidapi" || provider.id === "rapidapi_v2" || provider.id === "rapidapi_both";
      const endpoint = isRapidApi
        ? `${API}/campaigns/${analysisId}/whatsapp/rapidapi`
        : `${API}/campaigns/${analysisId}/whatsapp/send`;
      const { ok } = await apiCall("POST", endpoint);
      if (ok) { toast.success("Verificacion WhatsApp iniciada automaticamente"); fetchCampaigns(); }
    } catch {}
  };

  const fetchCampaigns = async (page, query, mode) => {
    try {
      const p = page || campaignsPage;
      const s = query !== undefined ? query : searchQuery;
      const m = mode !== undefined ? mode : filterMode;
      let url = `${API}/analyze/campaigns?page=${p}&limit=20`;
      if (s) url += `&search=${encodeURIComponent(s)}`;
      if (m) url += `&mode=${encodeURIComponent(m)}`;
      const { ok, data } = await apiCall("GET", url);
      if (!mountedRef.current) return;
      if (ok) {
        const newCampaigns = data.campaigns || [];
        setCampaigns(newCampaigns);
        setCampaignsCount(data.count || 0);
        setCampaignsPages(data.pages || 1);
        const currentActive = new Set(newCampaigns.filter(c => c.status === "processing" || c.status === "queued" || c.status === "uploading").map(c => c.analysis_id));
        const prevProcessing = prevProcessingRef.current;
        for (const id of prevProcessing) {
          if (!currentActive.has(id)) {
            const finished = newCampaigns.find(c => c.analysis_id === id && c.status === "completed");
            if (finished) {
              toast.success(`"${finished.campaign_name}" completada`, { duration: 6000 });
              playNotificationSound();
              if (selectedCampaign?.analysis_id === id) {
                loadCampaignDetails(finished, previewFilter, 1);
              }
            }
          }
        }
        prevProcessingRef.current = currentActive;
        const waActive = newCampaigns.some(c => c.globalwpp?.status && c.globalwpp?.webhook_status !== "completed");
        const waPending = newCampaigns.some(c => !c.globalwpp?.status && !["processing", "queued"].includes(c.status) && (c.campaign_mode === "phone_whatsapp" || c.campaign_mode === "whatsapp_only") && (c.summary?.celular || 0) > 0);
        if ((currentActive.size > 0 || waActive || waPending) && !pollingRef.current) {
          pollingRef.current = setInterval(() => fetchCampaigns(), 8000);
        } else if (currentActive.size === 0 && !waActive && !waPending && pollingRef.current) {
          clearInterval(pollingRef.current); pollingRef.current = null;
        }
      }
    } catch {}
  };

  const handleDrop = useCallback((e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length > 0) setBulkFile(e.dataTransfer.files[0]); }, []);
  const handleFileSelect = (e) => { if (e.target.files.length > 0) setBulkFile(e.target.files[0]); };

  const handleStartAnalysis = async () => {
    if (!bulkFile) return toast.error("Selecciona un archivo");
    if (!campaignName.trim()) return toast.error("Ingresa un nombre");
    setLoading(true);
    const CHUNK_SIZE = 400 * 1024;
    try {
      const arrayBuffer = await bulkFile.arrayBuffer();
      const totalChunks = Math.ceil(arrayBuffer.byteLength / CHUNK_SIZE);
      const initResult = await apiCall("POST", `${API}/upload/init`, {
        filename: bulkFile.name, campaign_name: campaignName.trim(),
        total_chunks: totalChunks, file_size: arrayBuffer.byteLength, campaign_mode: campaignMode,
      });
      if (!initResult.ok) {
        throw new Error(initResult.data.detail);
      }
      const { upload_id } = initResult.data;
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, arrayBuffer.byteLength);
        const chunk = arrayBuffer.slice(start, end);
        const bytes = new Uint8Array(chunk);
        let binary = "";
        for (let j = 0; j < bytes.length; j++) binary += String.fromCharCode(bytes[j]);
        await apiCall("POST", `${API}/upload/${upload_id}/chunk`, { chunk_index: i, data: btoa(binary) });
      }
      const completeResult = await apiCall("POST", `${API}/upload/${upload_id}/complete`);
      if (!completeResult.ok) throw new Error(completeResult.data.detail);
      toast.success(`"${campaignName}" creada - procesando archivo...`);
      setBulkFile(null); setCampaignName(""); setCampaignMode("phone");
      setScreen("list");
      if (!pollingRef.current) pollingRef.current = setInterval(() => fetchCampaigns(), 8000);
      fetchCampaigns();
    } catch (error) { toast.error(error.message || "Error"); }
    finally { setLoading(false); }
  };

  const openCampaignDetail = (campaign) => {
    if (campaign.status === "processing" || campaign.status === "queued" || campaign.status === "uploading" || campaign.status === "error") {
      setSelectedCampaign({ ...campaign, results: [] });
      setScreen("detail");
      return;
    }
    loadCampaignDetails(campaign, "all", 1);
    setScreen("detail");
  };

  const loadCampaignDetails = async (campaign, filter = "all", page = 1) => {
    setLoadingPreview(true);
    try {
      const { ok, data } = await apiCall("GET", `${API}/analyze/phones/${campaign.analysis_id}/status?filter_by=${filter}&page=${page}&page_size=100`);
      if (ok) { setSelectedCampaign(data); setFilteredTotal(data.filtered_total || 0); setPreviewPage(page); setPreviewFilter(filter); }
    } catch { toast.error("Error al cargar"); }
    finally { setLoadingPreview(false); }
  };

  const handleExport = async (filterType, format) => {
    if (!selectedCampaign?.analysis_id) return;
    const toastId = toast.loading("Generando exportacion...");
    try {
      // Start async export
      const { ok, data } = await apiCall("POST", `${API}/analyze/phones/${selectedCampaign.analysis_id}/export/generate?filter_type=${filterType}&format=${format}`);
      if (!ok) { toast.error("Error al generar", { id: toastId }); return; }
      const exportId = data.export_id;
      // Poll until ready
      for (let i = 0; i < 90; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const { ok: sOk, data: sData } = await apiCall("GET", `${API}/exports/${exportId}/status`);
        if (sOk && sData.status === "ready") {
          // Download via fetch+blob
          const resp = await fetch(`${API}/exports/${exportId}/download`, { credentials: "include" });
          if (!resp.ok) { toast.error("Error al descargar", { id: toastId }); return; }
          const blob = await resp.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a"); a.href = url; a.download = sData.filename || `export.${format}`; a.click();
          URL.revokeObjectURL(url);
          toast.success("Descargado", { id: toastId });
          return;
        }
        if (sOk && sData.status === "error") { toast.error("Error al generar archivo", { id: toastId }); return; }
      }
      toast.error("Timeout - intente de nuevo", { id: toastId });
    } catch (e) { toast.error("Error: " + e.message, { id: toastId }); }
  };

  const handleExportWa = async (filterBy, format) => {
    if (!selectedCampaign?.wpp_campaign_id) return;
    const toastId = toast.loading("Generando exportacion...");
    try {
      const resp = await fetch(`${API}/whatsapp/campaigns/${selectedCampaign.wpp_campaign_id}/export?filter_by=${filterBy}&format=${format}`, { credentials: "include" });
      if (!resp.ok) { toast.error("Error al exportar", { id: toastId }); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const disp = resp.headers.get("content-disposition");
      const fname = disp ? disp.split("filename=")[1]?.replace(/"/g, "") : `whatsapp_export.${format}`;
      const a = document.createElement("a"); a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success("Descargado", { id: toastId });
    } catch (e) { toast.error("Error: " + e.message, { id: toastId }); }
  };

  const handleDeleteCampaign = async (analysisId) => {
    try {
      const { ok, data } = await apiCall("DELETE", `${API}/analyze/campaigns/${analysisId}`);
      if (!ok) throw new Error(data.detail);
      toast.success("Eliminada");
      setCampaignToDelete(null);
      if (selectedCampaign?.analysis_id === analysisId) { setSelectedCampaign(null); setScreen("list"); }
      fetchCampaigns();
    } catch (error) { toast.error(error.message); }
  };

  const handleRetryCampaign = async (analysisId) => {
    const toastId = toast.loading("Reintentando...");
    try {
      const { ok, data } = await apiCall("POST", `${API}/analyze/campaigns/${analysisId}/retry`);
      if (!ok) throw new Error(data.detail);
      toast.success("Campana reiniciada", { id: toastId });
      fetchCampaigns();
      if (selectedCampaign?.analysis_id === analysisId) {
        setSelectedCampaign(prev => ({ ...prev, status: "processing", progress: 0, error: null }));
      }
    } catch (error) { toast.error(error.message, { id: toastId }); }
  };


  const handleDeleteAllCampaigns = async () => {
    try {
      const { ok } = await apiCall("DELETE", `${API}/analyze/campaigns`);
      if (ok) { toast.success("Todas eliminadas"); setShowDeleteAllDialog(false); setSelectedCampaign(null); setScreen("list"); fetchCampaigns(); }
    } catch (error) { toast.error(error.message); }
  };

  const handleSendWhatsapp = async () => {
    if (!selectedCampaign?.analysis_id) return;
    setWhatsappSending(true); setShowWhatsappConfirm(false);
    const toastId = toast.loading("Verificando WhatsApp...");
    try {
      const isRapidApi = selectedProvider === "rapidapi" || selectedProvider === "rapidapi_v2" || selectedProvider === "rapidapi_both";
      const endpoint = isRapidApi
        ? `${API}/campaigns/${selectedCampaign.analysis_id}/whatsapp/rapidapi`
        : `${API}/campaigns/${selectedCampaign.analysis_id}/whatsapp/send`;
      const { ok, data } = await apiCall("POST", endpoint);
      if (!ok) throw new Error(data.detail);
      toast.dismiss(toastId);
      toast.success(`Verificando ${data.sent_to_verify || data.sent_to_globalwpp || 0} numeros...`);
      loadCampaignDetails({ analysis_id: selectedCampaign.analysis_id, status: "completed" }, previewFilter, previewPage);
    } catch (error) { toast.dismiss(toastId); toast.error(error.message); }
    finally { setWhatsappSending(false); }
  };

  const handleImportWhatsapp = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCampaign?.analysis_id) return;
    setWhatsappImporting(true);
    try {
      const formData = new FormData(); formData.append("file", file);
      const { ok, data } = await apiCall("POST", `${API}/campaigns/${selectedCampaign.analysis_id}/whatsapp/import`, formData);
      if (!ok) throw new Error(data.detail);
      toast.success(`Importado: ${data.tiene_whatsapp} con WA, ${data.no_tiene_whatsapp} sin WA`);
      loadCampaignDetails({ analysis_id: selectedCampaign.analysis_id, status: "completed" }, previewFilter, previewPage);
    } catch (error) { toast.error(error.message); }
    finally { setWhatsappImporting(false); e.target.value = ""; }
  };

  const refreshWhatsappStatus = async () => {
    if (!selectedCampaign?.analysis_id) return;
    try {
      const { ok, data } = await apiCall("GET", `${API}/campaigns/${selectedCampaign.analysis_id}/whatsapp/status`);
      if (ok) {
        setSelectedCampaign(prev => ({ ...prev, globalwpp: { ...prev?.globalwpp, ...data } }));
        if (data.webhook_status === "completed" && wppPollingRef.current) {
          clearInterval(wppPollingRef.current); wppPollingRef.current = null;
          loadCampaignDetails({ analysis_id: selectedCampaign.analysis_id, status: "completed" }, previewFilter, previewPage);
        }
      }
    } catch {}
  };

  useEffect(() => {
    if (wppPollingRef.current) { clearInterval(wppPollingRef.current); wppPollingRef.current = null; }
    const gwpp = selectedCampaign?.globalwpp;
    if ((gwpp?.status === "sent" || gwpp?.status === "completed_rapidapi") && gwpp?.webhook_status !== "completed") {
      wppPollingRef.current = setInterval(refreshWhatsappStatus, 8000);
    }
    return () => { if (wppPollingRef.current) { clearInterval(wppPollingRef.current); wppPollingRef.current = null; } };
  }, [selectedCampaign?.analysis_id, selectedCampaign?.globalwpp?.status, selectedCampaign?.globalwpp?.webhook_status]);

  // ========== RENDER: CAMPAIGN LIST ==========
  if (screen === "list") {
    const processingCount = campaigns.filter(c => c.status === "processing").length;
    const queuedCount = campaigns.filter(c => c.status === "queued").length;
    const uploadingCount = campaigns.filter(c => c.status === "uploading").length;

    return (
      <div data-testid="campaigns-list-screen">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white tracking-tight">Campanas</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-zinc-500">{campaignsCount} total{services?.max_campaigns ? ` / ${services.max_campaigns} max` : ""}</span>
              {processingCount > 0 && <span className="text-xs text-amber-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />{processingCount} procesando</span>}
              {queuedCount > 0 && <span className="text-xs text-indigo-400 flex items-center gap-1"><Timer className="w-3 h-3" />{queuedCount} en cola</span>}
              {uploadingCount > 0 && <span className="text-xs text-violet-400 flex items-center gap-1"><Upload className="w-3 h-3 animate-pulse" />{uploadingCount} subiendo</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {waEnabled && (
              <button
                onClick={toggleWaAuto}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${waAutoEnabled ? "bg-green-600/20 text-green-400 border border-green-500/30 hover:bg-green-600/30" : "bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700"}`}
                data-testid="wa-auto-toggle"
              >
                {waAutoEnabled ? <><Play className="w-3.5 h-3.5" />WA Activo</> : <><Pause className="w-3.5 h-3.5" />WA Pausado</>}
              </button>
            )}
            <Button variant="ghost" size="icon" onClick={fetchCampaigns} className="text-zinc-400 hover:text-white" data-testid="refresh-campaigns"><RefreshCw className="w-4 h-4" /></Button>
            {campaigns.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setShowDeleteAllDialog(true)} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"><Trash2 className="w-4 h-4 mr-1" />Todas</Button>
            )}
            <Button onClick={() => setScreen("new")} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="new-campaign-btn">
              <Plus className="w-4 h-4 mr-1.5" />Nueva
            </Button>
          </div>
        </div>

        {/* Search and filters */}
        <div className="flex items-center gap-2 mb-4" data-testid="campaigns-search-bar">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCampaignsPage(1);
                clearTimeout(window._searchTimer);
                window._searchTimer = setTimeout(() => fetchCampaigns(1, e.target.value), 400);
              }}
              className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-white placeholder-zinc-600 focus:border-violet-500 focus:outline-none"
              data-testid="campaigns-search-input"
            />
          </div>
          <select
            value={filterMode}
            onChange={(e) => { setFilterMode(e.target.value); setCampaignsPage(1); fetchCampaigns(1, undefined, e.target.value); }}
            className="bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-300 px-3 py-2 focus:border-violet-500 focus:outline-none"
            data-testid="campaigns-filter-mode"
          >
            <option value="">Todos los tipos</option>
            <option value="phone">Solo Tel</option>
            <option value="phone_whatsapp">Tel + WA</option>
            <option value="whatsapp_only">Solo WA</option>
          </select>
        </div>

        {campaigns.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-zinc-800 rounded-xl">
            <Layers className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
            <p className="text-zinc-500 mb-1">No hay campanas aun</p>
            <p className="text-xs text-zinc-600 mb-5">Sube un archivo Excel o CSV para comenzar</p>
            <Button onClick={() => setScreen("new")} className="bg-violet-600 hover:bg-violet-700 text-white">
              <Plus className="w-4 h-4 mr-2" />Crear primera campana
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {campaigns.map(c => {
              const isProcessing = c.status === "processing";
              const isQueued = c.status === "queued";
              const isUploading = c.status === "uploading";
              const isError = c.status === "error";
              const hasWa = c.globalwpp?.status;
              const waSi = c.globalwpp?.whatsapp_si || c.globalwpp?.whatsapp_count || 0;
              const waNo = c.globalwpp?.whatsapp_no || c.globalwpp?.no_whatsapp_count || 0;
              const waErr = c.globalwpp?.error_count || 0;
              const waProcessing = hasWa && c.globalwpp?.webhook_status !== "completed";
              const waPendingStart = !hasWa && !isProcessing && !isQueued && !isUploading && !isError && (c.campaign_mode === "phone_whatsapp" || c.campaign_mode === "whatsapp_only") && (c.summary?.celular || 0) > 0;
              return (
                <div
                  key={c.analysis_id}
                  onClick={() => openCampaignDetail(c)}
                  className={`group p-4 rounded-xl border transition-all cursor-pointer ${
                    isError ? "bg-rose-500/5 border-rose-500/15 hover:border-rose-500/30" :
                    isUploading ? "bg-violet-500/5 border-violet-500/15 hover:border-violet-500/30" :
                    isQueued ? "bg-indigo-500/5 border-indigo-500/15 hover:border-indigo-500/30" :
                    isProcessing ? "bg-amber-500/5 border-amber-500/15 hover:border-amber-500/30" :
                    "bg-zinc-900/50 border-white/10 hover:border-violet-500/30"
                  }`}
                  data-testid={`campaign-card-${c.analysis_id}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <p className="font-semibold text-white truncate">{c.campaign_name}</p>
                      {c.source === "ultramsg" && (
                        <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px] shrink-0">WA</Badge>
                      )}
                      {c.campaign_mode && c.campaign_mode !== "phone" && (
                        <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px] shrink-0">
                          {c.campaign_mode === "phone_whatsapp" ? "Tel+WA" : "Solo WA"}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-3">
                      <StatusBadge status={c.status} progress={c.progress} queuePosition={c.queue_position} />
                      {waProcessing && <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px]"><RefreshCw className="w-2.5 h-2.5 mr-1 animate-spin" />WA</Badge>}
                      {waPendingStart && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px]"><Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" />WA</Badge>}
                      {isError && <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleRetryCampaign(c.analysis_id); }} className="opacity-0 group-hover:opacity-100 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-8 px-2 transition-opacity" data-testid={`retry-campaign-${c.analysis_id}`}>
                        <RefreshCw className="w-3.5 h-3.5 mr-1" />Reintentar
                      </Button>}
                      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setCampaignToDelete(c); }} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-rose-400 hover:bg-rose-500/10 h-8 w-8 p-0 transition-opacity" data-testid={`delete-campaign-${c.analysis_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {isProcessing && <Progress value={c.progress || 0} className="h-1.5 mb-2" />}
                  <div className="flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
                    <span>{formatDate(c.timestamp)}</span>
                    {!isQueued && !isUploading && <>
                      <span className="text-white font-medium">{(c.summary?.total || c.total_rows || 0).toLocaleString("es-AR")} nums</span>
                      <span className="text-emerald-400">{(c.summary?.celular || 0).toLocaleString("es-AR")} cel</span>
                      <span className="text-blue-400">{(c.summary?.fijo || 0).toLocaleString("es-AR")} fijos</span>
                      <span className="text-rose-400">{(c.summary?.invalido || 0).toLocaleString("es-AR")} inv</span>
                    </>}
                    {isUploading && <span className="text-violet-400">Procesando archivo...</span>}
                    {isQueued && <span className="text-indigo-400">{(c.total_rows || 0).toLocaleString("es-AR")} registros</span>}
                    {isError && c.error && <span className="text-rose-400 truncate max-w-[400px]">{c.error}</span>}
                    {hasWa && (() => {
                      const waTotal = waSi + waNo + waErr;
                      const celTotal = c.summary?.celular || 0;
                      const waPct = celTotal > 0 ? Math.round((waTotal / celTotal) * 100) : 0;
                      const waPending = Math.max(celTotal - waTotal, 0);
                      return (
                        <span className="text-green-400 flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          {waSi.toLocaleString("es-AR")} SI / {waNo.toLocaleString("es-AR")} NO
                          {waErr > 0 && <span className="text-rose-400">/ {waErr.toLocaleString("es-AR")} ERR</span>}
                          {waProcessing && waPending > 0 && <span className="text-zinc-500 ml-1">({waPct}% — faltan {waPending.toLocaleString("es-AR")})</span>}
                          {!waProcessing && waPct >= 100 && <span className="text-zinc-500 ml-1">(100%)</span>}
                        </span>
                      );
                    })()}
                    {waPendingStart && (
                      <span className="text-amber-400 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Iniciando verificacion WhatsApp...
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Pagination */}
        {campaignsPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4" data-testid="campaigns-pagination">
            <Button variant="outline" size="sm" disabled={campaignsPage <= 1}
              onClick={() => { setCampaignsPage(p => p - 1); fetchCampaigns(campaignsPage - 1); }}
              className="text-zinc-400 border-zinc-700 hover:text-white h-8 px-3">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs text-zinc-500">Pagina {campaignsPage} de {campaignsPages}</span>
            <Button variant="outline" size="sm" disabled={campaignsPage >= campaignsPages}
              onClick={() => { setCampaignsPage(p => p + 1); fetchCampaigns(campaignsPage + 1); }}
              className="text-zinc-400 border-zinc-700 hover:text-white h-8 px-3">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
        <Dialogs {...{showDeleteAllDialog, setShowDeleteAllDialog, campaignToDelete, setCampaignToDelete, campaignsCount, handleDeleteAllCampaigns, handleDeleteCampaign}} />
      </div>
    );
  }

  // ========== RENDER: NEW CAMPAIGN ==========
  if (screen === "new") {
    return (
      <div className="max-w-xl mx-auto" data-testid="new-campaign-screen">
        <Button variant="ghost" size="sm" onClick={() => setScreen("list")} className="text-zinc-400 hover:text-white mb-4" data-testid="back-to-list">
          <ChevronLeft className="w-4 h-4 mr-1" />Campanas
        </Button>
        <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-violet-600 flex items-center justify-center"><Upload className="w-5 h-5 text-white" /></div>
            <div>
              <h2 className="text-lg font-bold text-white">Nueva Campana</h2>
              <p className="text-sm text-zinc-400">Sube un archivo y procesa en segundo plano</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-zinc-300 mb-2 block">Nombre de la campana</Label>
              <Input value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder="Ej: SAT Marzo 2026" className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-500" data-testid="campaign-name-input" />
            </div>
            <div className={`rounded-xl p-6 text-center border-2 border-dashed transition-colors ${dragOver ? "border-violet-500 bg-violet-500/5" : "border-zinc-800 bg-zinc-950"}`}
              onDrop={handleDrop} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={e => { e.preventDefault(); setDragOver(false); }}>
              <input type="file" accept=".csv,.xlsx,.xls,.txt" onChange={handleFileSelect} className="hidden" id="bulk-file" />
              {bulkFile ? (
                <div className="space-y-2">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-violet-400" />
                  <p className="font-mono text-white text-sm">{bulkFile.name}</p>
                  <p className="text-xs text-zinc-500">{(bulkFile.size / 1024).toFixed(1)} KB</p>
                  <Button variant="ghost" size="sm" onClick={() => setBulkFile(null)} className="text-zinc-400 hover:text-white"><X className="w-4 h-4 mr-1" />Cambiar</Button>
                </div>
              ) : (
                <label htmlFor="bulk-file" className="cursor-pointer space-y-2 block">
                  <Upload className="w-10 h-10 mx-auto text-zinc-500" />
                  <p className="text-white text-sm">Arrastra o selecciona archivo</p>
                  <p className="text-xs text-zinc-500">Excel, CSV o TXT con columna de telefono</p>
                </label>
              )}
            </div>
            {waEnabled && (
              <div data-testid="campaign-mode-selector">
                <Label className="text-xs font-semibold text-zinc-400 mb-2 block">Modo de verificacion</Label>
                <div className="grid grid-cols-1 gap-1.5">
                  {[
                    { id: "phone", label: "Solo telefono", desc: "Clasifica cel/fijo/invalido" },
                    { id: "phone_whatsapp", label: "Telefono + WhatsApp", desc: "Clasifica y verifica WA automaticamente" },
                    { id: "whatsapp_only", label: "Solo WhatsApp", desc: "Verifica WA directamente" },
                  ].map(m => (
                    <button key={m.id} type="button" onClick={() => setCampaignMode(m.id)}
                      className={`flex items-center gap-2 p-2.5 rounded-lg border text-left transition-all text-xs ${campaignMode === m.id ? "bg-zinc-800 border-violet-500/50 ring-1 ring-violet-500/30" : "bg-zinc-950 border-zinc-800 hover:border-zinc-700"}`} data-testid={`mode-${m.id}`}>
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${campaignMode === m.id ? "bg-violet-500" : "bg-zinc-700"}`} />
                      <div><span className={`font-medium ${campaignMode === m.id ? "text-white" : "text-zinc-400"}`}>{m.label}</span><span className="text-zinc-600 ml-1.5">{m.desc}</span></div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={handleStartAnalysis} disabled={loading || !bulkFile || !campaignName.trim()} className="w-full bg-violet-600 hover:bg-violet-700 text-white h-11" data-testid="start-analysis-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Subiendo...</> : <><ShieldCheck className="w-4 h-4 mr-2" />Iniciar Analisis</>}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ========== RENDER: CAMPAIGN DETAIL ==========
  const sc = selectedCampaign;
  const isProcessing = sc?.status === "processing";
  const isQueued = sc?.status === "queued";
  const isUploading = sc?.status === "uploading";
  const isError = sc?.status === "error";
  const gwpp = sc?.globalwpp;
  const hasWa = gwpp?.status;
  const waComplete = gwpp?.webhook_status === "completed";
  const waPausedQuota = gwpp?.pause_reason === "quota_exceeded" || gwpp?.status === "paused_quota" || gwpp?.webhook_status === "paused_quota";
  const waSi = gwpp?.whatsapp_si || gwpp?.whatsapp_count || 0;
  const waNo = gwpp?.whatsapp_no || gwpp?.no_whatsapp_count || 0;
  const waErr = gwpp?.error_count || 0;
  const waSkipped = gwpp?.skipped_count || 0;

  return (
    <div data-testid="campaign-detail-screen">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => { setSelectedCampaign(null); setScreen("list"); }} className="text-zinc-400 hover:text-white" data-testid="back-to-list-detail">
          <ChevronLeft className="w-4 h-4 mr-1" />Campanas
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-white truncate">{sc?.campaign_name}</h2>
          <p className="text-xs text-zinc-500">{formatDate(sc?.timestamp)} {sc?.filename && `- ${sc.filename}`}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => loadCampaignDetails({ analysis_id: sc?.analysis_id, status: sc?.status || "completed" }, previewFilter, previewPage)} className="text-zinc-400 hover:text-white" data-testid="campaign-detail-refresh"><RefreshCw className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setCampaignToDelete(sc)} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Uploading indicator */}
      {isUploading && (
        <div className="p-4 bg-violet-500/10 border border-violet-500/20 rounded-xl mb-4" data-testid="uploading-indicator">
          <div className="flex items-center gap-3">
            <Upload className="w-5 h-5 text-violet-400 animate-pulse" />
            <div>
              <p className="text-sm text-violet-300 font-medium">Procesando archivo...</p>
              <p className="text-xs text-violet-400/60 mt-0.5">El archivo esta siendo leido y validado. La verificacion comenzara automaticamente.</p>
            </div>
          </div>
        </div>
      )}

      {/* Queue indicator */}
      {isQueued && (
        <div className="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl mb-4" data-testid="queued-indicator">
          <div className="flex items-center gap-3">
            <Timer className="w-5 h-5 text-indigo-400" />
            <div>
              <p className="text-sm text-indigo-300 font-medium">En cola de procesamiento</p>
              <p className="text-xs text-indigo-400/60 mt-0.5">Hay otra campana procesandose. Esta comenzara automaticamente cuando termine.</p>
            </div>
          </div>
        </div>
      )}

      {/* Processing indicator */}
      {isProcessing && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl mb-4" data-testid="processing-indicator">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm text-amber-300">Procesando numeros...</span>
            <span className="text-sm font-bold text-amber-400">{sc?.progress || 0}%</span>
          </div>
          <Progress value={sc?.progress || 0} className="h-2 bg-zinc-800" />
        </div>
      )}

      {/* Error indicator */}
      {isError && (
        <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl mb-4" data-testid="error-indicator">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-400" />
              <div>
                <p className="text-sm text-rose-300 font-medium">Error en la campana</p>
                <p className="text-xs text-rose-400/60 mt-0.5">{sc?.error || "Ocurrio un error durante el procesamiento."}</p>
              </div>
            </div>
            <Button size="sm" onClick={() => handleRetryCampaign(sc?.analysis_id)} className="bg-amber-600 hover:bg-amber-700 text-white shrink-0" data-testid="retry-campaign-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Reintentar
            </Button>
          </div>
        </div>
      )}

      {!isProcessing && !isQueued && !isUploading && !isError && (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            {[
              { label: "Total", value: sc?.summary?.total || 0, color: "text-white", bg: "bg-zinc-900/50 border-white/10" },
              { label: "Celulares", value: sc?.summary?.celular || 0, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
              { label: "Fijos", value: sc?.summary?.fijo || 0, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" },
              { label: "Invalidos", value: sc?.summary?.invalido || 0, color: "text-rose-400", bg: "bg-rose-500/10 border-rose-500/20" },
              { label: "Normalizados", value: sc?.summary?.normalizados || 0, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
            ].map(s => (
              <div key={s.label} className={`p-3 rounded-xl border text-center ${s.bg}`}>
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-[10px] uppercase tracking-wider opacity-60">{s.label}</p>
              </div>
            ))}
          </div>

          {/* WhatsApp: Pending auto-trigger indicator */}
          {waEnabled && !hasWa && (sc?.summary?.celular || 0) > 0 && (sc?.campaign_mode === "phone_whatsapp" || sc?.campaign_mode === "whatsapp_only") && (
            <div className="flex items-center gap-2 p-3 bg-green-500/5 border border-green-500/20 rounded-xl mb-4" data-testid="wa-pending">
              <Loader2 className="w-4 h-4 text-green-400 animate-spin" />
              <p className="text-xs text-green-400">Verificacion WhatsApp iniciando automaticamente para {(sc?.summary?.celular || 0).toLocaleString("es-AR")} celulares...</p>
            </div>
          )}

          {/* WhatsApp Section - show when there are results */}
          {waEnabled && hasWa && (sc?.summary?.celular || 0) > 0 && (
            <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-4 mb-4" data-testid="wa-section">
              <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-3"><MessageCircle className="w-4 h-4 text-green-400" />WhatsApp <span className="text-zinc-500 font-normal">({sc?.summary?.celular || 0} celulares)</span></h3>
              <div className="space-y-3">
                {waPausedQuota && (
                  <div className="flex items-start gap-2 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg" data-testid="wa-paused-quota-banner">
                    <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
                    <div className="text-xs text-orange-200 leading-relaxed">
                      <p className="font-semibold text-orange-300 mb-1">Cuota de la API de WhatsApp agotada</p>
                      <p className="text-orange-200/80">La verificación se pausó automáticamente para no consumir más del archivo. Los números ya verificados quedaron guardados (los ERROR <b>no se cobraron</b>). Renová o actualizá tu plan en <a href="https://rapidapi.com/domainskbgroup/api/whatsapp-number-validators" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-100">RapidAPI</a> y reanudá desde el panel de admin.</p>
                    </div>
                  </div>
                )}
                {!waComplete && !waPausedQuota && (
                  <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <RefreshCw className="w-3.5 h-3.5 text-amber-400 animate-spin" />
                    <span className="text-xs text-amber-300">Verificando... Los resultados se actualizan automaticamente.</span>
                    <Button variant="ghost" size="sm" onClick={refreshWhatsappStatus} className="text-amber-400 hover:text-amber-300 ml-auto text-xs h-6"><RefreshCw className="w-3 h-3 mr-1" />Actualizar</Button>
                  </div>
                )}
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700 text-center">
                    <p className="text-xl font-bold text-white">{sc?.summary?.celular || 0}</p>
                    <p className="text-[10px] text-zinc-500">Total Celulares</p>
                  </div>
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                    <p className="text-xl font-bold text-green-400">{waSi}</p>
                    <p className="text-[10px] text-green-400/60">Tiene WA</p>
                  </div>
                  <div className="p-3 rounded-lg bg-zinc-800 border border-zinc-700 text-center">
                    <p className="text-xl font-bold text-zinc-400">{waNo}</p>
                    <p className="text-[10px] text-zinc-500">No tiene</p>
                  </div>
                  {waErr > 0 && (
                    <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-center">
                      <p className="text-xl font-bold text-rose-400">{waErr}</p>
                      <p className="text-[10px] text-rose-400/60">Error API</p>
                    </div>
                  )}
                  {(() => {
                    const unverified = (sc?.summary?.celular || 0) - waSi - waNo - waErr - waSkipped;
                    return unverified > 0 ? (
                      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
                        <p className="text-xl font-bold text-amber-400">{unverified}</p>
                        <p className="text-[10px] text-amber-400/60">Sin verificar</p>
                      </div>
                    ) : null;
                  })()}
                  {waSkipped > 0 && (
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
                      <p className="text-xl font-bold text-amber-400">{waSkipped}</p>
                      <p className="text-[10px] text-amber-400/60">Sin formato</p>
                    </div>
                  )}
                </div>
                {isSuperadmin && waComplete && (
                  <ReverifyCachedPanel analysisId={sc?.analysis_id} reverify={gwpp?.reverify}
                    onRefresh={() => loadCampaignDetails({ analysis_id: sc?.analysis_id, status: sc?.status || "completed" }, previewFilter, previewPage)} />
                )}
                {gwpp?.provider !== "rapidapi" && !waComplete && (
                  <div className="flex items-center gap-2">
                    <input type="file" id="wpp-import" accept=".csv,.xlsx,.xls,.txt" onChange={handleImportWhatsapp} className="hidden" />
                    <label htmlFor="wpp-import"><Button asChild variant="outline" size="sm" disabled={whatsappImporting} className="bg-zinc-950 border-zinc-700 text-zinc-300 cursor-pointer"><span><Upload className="w-3 h-3 mr-1" />Importar Resultados</span></Button></label>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Export Section */}
          <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-4 mb-4" data-testid="export-section">
            <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-3"><Download className="w-4 h-4 text-violet-400" />Exportar</h3>
            <div className="flex flex-wrap gap-2">
              <ExportBtn filterType="all" count={sc?.summary?.total || 0} label="Todos" icon={Download} cls="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700" onExport={handleExport} />
              <ExportBtn filterType="celular" count={sc?.summary?.celular || 0} label="Celulares" icon={Phone} cls="bg-emerald-600 hover:bg-emerald-700 text-white" onExport={handleExport} />
              <ExportBtn filterType="fijo" count={sc?.summary?.fijo || 0} label="Fijos" icon={Phone} cls="bg-blue-600 hover:bg-blue-700 text-white" onExport={handleExport} />
              <ExportBtn filterType="invalido" count={sc?.summary?.invalido || 0} label="Invalidos" icon={AlertTriangle} cls="bg-rose-600 hover:bg-rose-700 text-white" onExport={handleExport} />
            </div>
            {(waSi > 0 || waNo > 0) && sc?.wpp_campaign_id && (
              <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t border-zinc-800">
                <span className="text-xs text-green-400 font-medium flex items-center gap-1 self-center"><MessageCircle className="w-3.5 h-3.5" />WhatsApp:</span>
                <ExportBtn filterType="all" count={sc?.summary?.celular || (waSi + waNo + waErr + waSkipped)} label="Todos WA" icon={Download} cls="bg-zinc-700 hover:bg-zinc-600 text-white border border-zinc-600" onExport={handleExportWa} />
                <ExportBtn filterType="si" count={waSi} label="Con WA" icon={MessageCircle} cls="bg-green-600 hover:bg-green-700 text-white" onExport={handleExportWa} />
                <ExportBtn filterType="no" count={waNo} label="Sin WA" icon={AlertTriangle} cls="bg-zinc-700 hover:bg-zinc-600 text-white border border-zinc-600" onExport={handleExportWa} />
                {waErr > 0 && <ExportBtn filterType="error" count={waErr} label="Errores" icon={AlertTriangle} cls="bg-rose-700 hover:bg-rose-600 text-white" onExport={handleExportWa} />}
              </div>
            )}
            {(waSi > 0 || waNo > 0) && !sc?.wpp_campaign_id && (
              <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t border-zinc-800">
                <span className="text-xs text-green-400 font-medium flex items-center gap-1 self-center"><MessageCircle className="w-3.5 h-3.5" />WhatsApp:</span>
                <ExportBtn filterType="whatsapp_all" count={sc?.summary?.celular || (waSi + waNo + waErr + waSkipped)} label="Todos WA" icon={Download} cls="bg-zinc-700 hover:bg-zinc-600 text-white border border-zinc-600" onExport={handleExport} />
                <ExportBtn filterType="whatsapp_si" count={waSi} label="Con WA" icon={MessageCircle} cls="bg-green-600 hover:bg-green-700 text-white" onExport={handleExport} />
                <ExportBtn filterType="whatsapp_no" count={waNo} label="Sin WA" icon={AlertTriangle} cls="bg-zinc-700 hover:bg-zinc-600 text-white border border-zinc-600" onExport={handleExport} />
                {waErr > 0 && <ExportBtn filterType="whatsapp_error" count={waErr} label="Errores" icon={AlertTriangle} cls="bg-rose-700 hover:bg-rose-600 text-white" onExport={handleExport} />}
              </div>
            )}
          </div>

          {/* Results Table */}
          {sc?.results?.length > 0 && (
            <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-4" data-testid="results-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-white">Resultados <span className="text-xs font-normal text-zinc-500">({filteredTotal})</span></h3>
                <Select value={previewFilter} onValueChange={(f) => { setPreviewFilter(f); loadCampaignDetails({ analysis_id: sc?.analysis_id, status: "completed" }, f, 1); }}>
                  <SelectTrigger className="w-32 bg-zinc-950 border-zinc-800 text-white text-xs h-8" data-testid="preview-filter"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="all" className="text-white">Todos</SelectItem>
                    <SelectItem value="celular" className="text-emerald-400">Celulares</SelectItem>
                    <SelectItem value="fijo" className="text-blue-400">Fijos</SelectItem>
                    <SelectItem value="invalido" className="text-rose-400">Invalidos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {loadingPreview ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-violet-400 mr-2" /><span className="text-zinc-500 text-sm">Cargando...</span></div>
              ) : (
                <>
                  <div className="rounded-lg border border-zinc-800 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="results-table">
                        <thead><tr className="bg-zinc-950 border-b border-zinc-800">
                          <th className="text-left px-2 py-2 text-[10px] font-semibold uppercase text-zinc-500">#</th>
                          <th className="text-left px-2 py-2 text-[10px] font-semibold uppercase text-zinc-500">Original</th>
                          <th className="text-left px-2 py-2 text-[10px] font-semibold uppercase text-zinc-500">Normalizado</th>
                          <th className="text-left px-2 py-2 text-[10px] font-semibold uppercase text-zinc-500">Tipo</th>
                          <th className="text-left px-2 py-2 text-[10px] font-semibold uppercase text-zinc-500">WA</th>
                          <th className="text-left px-2 py-2 text-[10px] font-semibold uppercase text-zinc-500">Prov</th>
                          <th className="text-left px-2 py-2 text-[10px] font-semibold uppercase text-zinc-500">Operador</th>
                        </tr></thead>
                        <tbody>
                          {sc.results.map((r, i) => (
                            <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 text-xs">
                              <td className="px-2 py-1.5 text-zinc-600 font-mono">{(previewPage - 1) * 100 + i + 1}</td>
                              <td className="px-2 py-1.5 font-mono text-white">{r.original_number}</td>
                              <td className="px-2 py-1.5 font-mono text-violet-400">{r.numero_normalizado || "\u2014"}{r.quito_15 && <span className="text-amber-400/70 ml-0.5">*</span>}</td>
                              <td className="px-2 py-1.5"><Badge className={`text-[10px] ${r.tipo === "CELULAR" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : r.tipo === "FIJO" ? "bg-blue-500/10 text-blue-400 border-blue-500/20" : "bg-rose-500/10 text-rose-400 border-rose-500/20"}`}>{r.tipo}</Badge></td>
                              <td className="px-2 py-1.5">{r.tiene_whatsapp === "SI" ? <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px]">SI</Badge> : r.tiene_whatsapp === "NO" ? <Badge className="bg-zinc-700 text-zinc-400 text-[10px]">NO</Badge> : r.tiene_whatsapp === "ERROR" ? <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20 text-[10px]">ERROR</Badge> : <span className="text-zinc-700">{"\u2014"}</span>}</td>
                              <td className="px-2 py-1.5 text-zinc-400">{r.provincia || "\u2014"}</td>
                              <td className="px-2 py-1.5 text-zinc-500 truncate max-w-[120px]">{r.operador || "\u2014"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {filteredTotal > 100 && (
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-zinc-500">Pag {previewPage} de {Math.ceil(filteredTotal / 100)}</span>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={previewPage <= 1} onClick={() => loadCampaignDetails({ analysis_id: sc?.analysis_id, status: "completed" }, previewFilter, previewPage - 1)} className="bg-zinc-950 border-zinc-800 text-zinc-300 text-xs h-7">Anterior</Button>
                        <Button variant="outline" size="sm" disabled={previewPage >= Math.ceil(filteredTotal / 100)} onClick={() => loadCampaignDetails({ analysis_id: sc?.analysis_id, status: "completed" }, previewFilter, previewPage + 1)} className="bg-zinc-950 border-zinc-800 text-zinc-300 text-xs h-7">Siguiente</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Dialogs */}
      <Dialogs {...{showDeleteAllDialog, setShowDeleteAllDialog, campaignToDelete, setCampaignToDelete, campaignsCount, handleDeleteAllCampaigns, handleDeleteCampaign}} />
    </div>
  );
};

const Dialogs = ({ showDeleteAllDialog, setShowDeleteAllDialog, campaignToDelete, setCampaignToDelete, campaignsCount, handleDeleteAllCampaigns, handleDeleteCampaign }) => (
  <>
    <AlertDialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
      <AlertDialogContent className="bg-zinc-900 border-zinc-800">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Eliminar todas?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400">Se eliminaran las {campaignsCount} campanas.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white">Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleDeleteAllCampaigns} className="bg-rose-600 text-white">Eliminar Todas</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <AlertDialog open={!!campaignToDelete} onOpenChange={() => setCampaignToDelete(null)}>
      <AlertDialogContent className="bg-zinc-900 border-zinc-800">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Eliminar?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400">Se eliminara "{campaignToDelete?.campaign_name}"</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white">Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={() => handleDeleteCampaign(campaignToDelete?.analysis_id)} className="bg-rose-600 text-white">Eliminar</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
);
